import Service from './Service';
import {
  CommentSort,
  CommentVoteResult,
  ForumComment,
  ForumThread,
  ModerationComment,
  ModerationQueueStatus,
  ModerationStats,
  RejectReason,
} from '../@types/Forum';

export interface CreateCommentBody {
  body: string;
  /** responder a um comentário de topo; aninhamento é de 1 nível só */
  parentId?: string;
}

export interface ModerationListParams {
  status: ModerationQueueStatus;
  /** busca por aluno, nº da questão ou trecho do comentário */
  q?: string;
  subjectId?: string;
}

export interface RejectCommentBody {
  reason: RejectReason;
  /** mensagem opcional ao aluno, mostrada junto do motivo no fórum */
  message?: string;
}

/**
 * Comentários do fórum da questão + a fila de moderação de comentários.
 *
 * As denúncias de **questão** ficam em `QuestionReportService` de propósito
 * (decisão 1 do design): os dois domínios evoluem separados.
 *
 * ⚠️ Nenhuma dessas rotas existe no Go API ainda — ver o contrato assumido em
 * `openspec/changes/add-question-forum/design.md`.
 */
class ForumService extends Service {
  /* ---------------------------------------------------------------- aluno */

  /** Aprovados + os do próprio usuário em qualquer estado, com `replies`. */
  static listComments(questionId: string, sort: CommentSort = 'useful') {
    return this.Http.get<ForumThread>(`/questions/${questionId}/comments`, {
      params: { sort },
    }).then(this.getData);
  }

  /** `201 Comment` — nasce `pending`, ou `approved` direto se admin/master. */
  static createComment(questionId: string, body: CreateCommentBody) {
    return this.Http.post<ForumComment>(`/questions/${questionId}/comments`, body).then(
      this.getData
    );
  }

  /** Edita o próprio comentário; se o autor é aluno, volta a `pending`. */
  static updateComment(commentId: string, body: string) {
    return this.Http.put<ForumComment>(`/comments/${commentId}`, { body }).then(this.getData);
  }

  /** Toggle do voto "útil" — clicar de novo remove. */
  static voteComment(commentId: string) {
    return this.Http.post<CommentVoteResult>(`/comments/${commentId}/vote`).then(this.getData);
  }

  /** Denuncia um comentário: ele vira `flagged` e reentra na fila. `409` se já denunciou. */
  static flagComment(commentId: string) {
    return this.Http.post(`/comments/${commentId}/flag`).then(this.getData);
  }

  /* ------------------------------------------------------------ moderação */

  static listModerationComments(params: ModerationListParams) {
    // O Go manda a referência da questão como `question {questionId, shortRef,
    // …}`; o vocabulário canônico do front é `QuestionRef {id, number, …}` —
    // a tradução mora aqui, não na tela.
    return this.Http.get<any[]>('/moderation/comments', { params })
      .then(this.getData)
      .then((rows): ModerationComment[] =>
        (Array.isArray(rows) ? rows : []).map((row) => ({
          ...row,
          question: row?.question
            ? {
                ...row.question,
                id: String(row.question.id ?? row.question.questionId ?? row.questionId ?? ''),
                number: String(row.question.number ?? row.question.shortRef ?? '').replace(/^#/, ''),
              }
            : undefined,
        }))
      );
  }

  /**
   * Aprova um comentário pendente. `body` opcional permite editar-e-aprovar,
   * preservando o autor original.
   */
  static approveComment(commentId: string, body?: string) {
    return this.Http.post(
      `/moderation/comments/${commentId}/approve`,
      body === undefined ? {} : { body }
    ).then(this.getData);
  }

  /** Aprovação em lote — "Aprovar selecionados (N)". */
  static approveCommentsBatch(ids: string[]) {
    return this.Http.post('/moderation/comments/approve-batch', { ids }).then(this.getData);
  }

  /** Rejeita com motivo obrigatório e mensagem opcional ao aluno. */
  static rejectComment(commentId: string, body: RejectCommentBody) {
    return this.Http.post(`/moderation/comments/${commentId}/reject`, body).then(this.getData);
  }

  /** KPIs do dia + contagens que alimentam os badges das abas. */
  static getModerationStats() {
    return this.Http.get<ModerationStats>('/moderation/stats').then(this.getData);
  }
}

export default ForumService;
